import React, {useContext, useEffect, useMemo, useState} from "react";
import {getUserSignDocsAPI} from "../../../Services/api";
import {isEmpty, map, get} from "lodash";
import {SignDocCard} from "../../SignDoc/SignDocCard/SignDocCard";
import {CompanyReactContext} from "../../../contexts/CompanyContext";
import {I18nReactContext} from "../../../contexts/I18nContext";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import {getSignDocsToDo, getCompletedSignDocs} from "../../../Utils/SignDocs/SignDocs";
import './MySignDocsContent.scss'

/**
 * MySignDocsContent - Renders user's SignDocs split into TODO and COMPLETED sections
 *
 * This component handles data fetching and rendering for user view.
 * Filtering logic is provided by the parent (SignDocsListWithFilters).
 *
 * @param {Array} types - Array of SignDoc types to fetch
 * @param {Array} categories - Array of categories to fetch
 * @param {Function} filterSignDocsBySearch - Filter function from parent
 * @param {boolean} showForceCompletion - Show force completion SignDocs
 * @param {boolean} showNonForceCompletion - Show non-force completion SignDocs
 * @param {boolean} hideLearningPathItems - Hide learning path items
 * @param {boolean} showOnlyUncompletedTasks - Show only uncompleted tasks (no completed section)
 */
export const MySignDocsContent = ({
    types,
    categories,
    filterSignDocsBySearch,
    showForceCompletion = false,
    showNonForceCompletion = false,
    hideLearningPathItems = true,
    showOnlyUncompletedTasks = false
}) => {
    const [completedSignDocs, setCompletedSignDocs] = useState([])
    const [signDocsToDo, setSignDocsToDo] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const companyContext = useContext(CompanyReactContext)
    const { i18n } = useContext(I18nReactContext)

    const initSignDocs = async () => {
        setIsLoading(true)
        try {
            const userSignDocs = await getUserSignDocsAPI({
                types: types,
                categories: categories,
                showForceCompletion,
                showNonForceCompletion,
                hideLearningPathItems
            })

            const completedSignDocs = getCompletedSignDocs({userSignDocs});
            const signDocsToDo = getSignDocsToDo({userSignDocs, showNonForceCompletion})
            setCompletedSignDocs(completedSignDocs)
            setSignDocsToDo(signDocsToDo)
        } finally {
            setIsLoading(false)
        }
    }

    // Apply parent's filter function to both TODO and completed
    const filteredSignDocsToDo = useMemo(() => {
        return filterSignDocsBySearch({signDocs: signDocsToDo});
    }, [signDocsToDo, filterSignDocsBySearch]);

    const filteredCompletedSignDocs = useMemo(() => {
        return filterSignDocsBySearch({signDocs: completedSignDocs});
    }, [completedSignDocs, filterSignDocsBySearch]);

    // Determine if empty state is due to filters or truly empty
    const isEmptyDueToFilters = useMemo(() => {
        // If we have original data but filtered data is empty, it's due to filters
        return (signDocsToDo.length > 0 && filteredSignDocsToDo.length === 0) ||
               (completedSignDocs.length > 0 && filteredCompletedSignDocs.length === 0);
    }, [signDocsToDo.length, filteredSignDocsToDo.length, completedSignDocs.length, filteredCompletedSignDocs.length]);

    const init = async () => {
        initSignDocs()
    }

    useEffect(() => {
        if(isEmpty(companyContext.featureFlags)){
            return
        }
        init()
        const interval = setInterval(() => {
            init()
        }, 60000);
        return () => clearInterval(interval);
    }, [companyContext.featureFlags]);

    if (isLoading) {
        return <LoadingSpinner local={true} />;
    }

    const containerClassName = showOnlyUncompletedTasks ? "MyOpenTasks" : "MySignDocs";
    const wrapperClassName = showOnlyUncompletedTasks ? "OpenTasksWrapper" : "SignDocsWrapper";
    const sectionClassName = showOnlyUncompletedTasks ? "openTasksSection" : "signDocsSection";
    const emptyClassName = showOnlyUncompletedTasks ? "noOpenTasks" : "noSignDocsToDo";

    return <div className={containerClassName}>
            {!showOnlyUncompletedTasks && (
                <div className="PageSecondaryTitle signDocsTitle">
                    {i18n.translate('learningCenter.signDocsToDo')}
                </div>
            )}
            <div className={sectionClassName}>
                {!filteredSignDocsToDo || filteredSignDocsToDo.length === 0 ? (
                    <div className={emptyClassName}>
                        {isEmptyDueToFilters
                            ? i18n.translate('LearningManagementPage.noSearchResults')
                            : i18n.translate('learningCenter.noSignDocsToDo')
                        }
                    </div>
                ) : (
                    <div className={wrapperClassName}>
                        {map(filteredSignDocsToDo, (signDoc) => {
                            const finishByDate = signDoc.finishByDate || null
                            return <div className="signDocWrapper" key={signDoc.id}>
                                <SignDocCard {...{...signDoc, ...signDoc.signDoc, additionalData: get(signDoc, 'additionalData', {}), signDocAdditionalData: get(signDoc, 'signDoc.additionalData', {})}} finishByDate={finishByDate} key={signDoc.id}/>
                            </div>
                        })}
                    </div>
                )}
            </div>
            {!showOnlyUncompletedTasks && (
                <>
                    <div className="PageSecondaryTitle signDocsTitle">
                        {i18n.translate('learningCenter.signDocsCompleted')}
                    </div>
                    <div className="signDocsSection">
                        {!filteredCompletedSignDocs || filteredCompletedSignDocs.length === 0 ? (
                            <div className="noSignDocsToDo">
                                {isEmptyDueToFilters
                                    ? i18n.translate('LearningManagementPage.noSearchResults')
                                    : i18n.translate('learningCenter.noSignDocsCompleted')
                                }
                            </div>
                        ) : (
                            <div className="SignDocsWrapper">
                                {map(filteredCompletedSignDocs, (signDoc) => {
                                    const finishByDate = signDoc.finishByDate || null
                                    return <div className="signDocWrapper" key={signDoc.id}>
                                        <SignDocCard {...{...signDoc, ...signDoc.signDoc, signDocAdditionalData: signDoc.signDoc?.additionalData}} finishByDate={finishByDate} key={signDoc.id}/>
                                    </div>
                                })}
                            </div>
                        )}
                    </div>
                </>
            )}
    </div>
}
